import axios from "axios";

import { getMessages } from "../../../i18n";
import { authApi } from "../../../services/api";

export interface CalendarSummary {
    /** Google's calendar id. Unique only WITHIN an account — two
     *  accounts both have a calendar called "primary" — so never use it
     *  alone as a React key or a selection key. Pair it with
     *  `account_id` via `sourceKey`. */
    id: string;
    /** Which connected Google account this calendar came from. */
    account_id: string;
    account_email: string | null;
    summary: string | null;
    primary: boolean;
    background_color?: string | null;
    /** Google's access role: "owner" / "writer" / "reader" /
     *  "freeBusyReader". Calendars a teammate shared read-only come
     *  back as "reader", and the UI disables creating on them rather
     *  than letting the user write and take a 403. */
    access_role?: string | null;
    /** Whether the calendar is ticked in the user's own Google Calendar
     *  UI. Used as the default selection so the first open mirrors what
     *  they already see on Google. */
    selected?: boolean;
}

/** An account whose calendars couldn't be listed. Reported alongside
 *  the calendars that DID load so one broken account degrades to a
 *  banner instead of an empty picker. */
export interface FailedAccount {
    account_id: string;
    reason: CalendarErrorKind | string;
}

export interface ConnectedCalendarAccount {
    id: string;
    email: string | null;
    label: string;
    is_primary: boolean;
}

export interface CalendarListResponse {
    calendars: CalendarSummary[];
    failed_accounts: FailedAccount[];
    accounts: ConnectedCalendarAccount[];
}

/** Identifies one (account, calendar) pair — the unit the calendar
 *  modal selects, colors, and fetches by. */
export interface CalendarSource {
    accountId: string;
    calendarId: string;
}

/** Stable string form of a source, used as a React key, a selection-set
 *  member, and the wire format for the aggregate endpoint's `sources`
 *  param. Calendar ids can contain ":" so the account id goes FIRST and
 *  the server splits on the first colon only. */
export const sourceKey = (accountId: string, calendarId: string): string =>
    `${accountId}:${calendarId}`;

/** Where an aggregated event came from. Server-attached, because the
 *  triple (account_id, calendar_id, id) is the only thing that
 *  identifies an event: ids are unique per calendar rather than
 *  globally, and the account decides which credential an edit is
 *  authenticated with. */
export interface CalendarEventSource {
    account_id: string;
    account_email: string | null;
    calendar_id: string;
}

/** A source that failed to load in an aggregate fetch. */
export interface FailedSource {
    account_id: string;
    calendar_id: string;
    reason: string;
}

export interface AggregateEventsResponse {
    items: CalendarEvent[];
    failed_sources: FailedSource[];
}

export interface CalendarEventDateTime {
    dateTime?: string;
    date?: string;
    timeZone?: string;
}

export interface CalendarConferenceEntryPoint {
    entryPointType?: string;
    label?: string;
    uri?: string;
}

export interface CalendarConferenceData {
    /** Present when Meet generation is in progress; see
     *  `status.statusCode === "pending"`. */
    createRequest?: {
        requestId?: string;
        status?: { statusCode?: "pending" | "success" | "failure" };
    };
    /** Present on already-created Meet conferences. */
    entryPoints?: CalendarConferenceEntryPoint[];
}

export interface CalendarEventAttendee {
    email: string;
    displayName?: string;
    /** Google-side RSVP. Read-only on the wire from our perspective
     *  — the modal doesn't surface it for v1; only used as a tag
     *  for future "going / maybe / declined" rendering. */
    responseStatus?: "needsAction" | "declined" | "tentative" | "accepted";
    /** Self attendees can be flagged so the UI dedupes the user
     *  out of attendee chips. */
    self?: boolean;
}

export interface CalendarEvent {
    /** Populated by Google when an event has invited attendees. The
     *  organizer is implicit and may or may not appear here
     *  depending on the calendar; never relied on as the source of
     *  truth for "who created this". */
    attendees?: CalendarEventAttendee[];
    conferenceData?: CalendarConferenceData;
    description?: string;
    end?: CalendarEventDateTime;
    /** Populated by Google when an event has an attached Meet. May be
     *  absent on the create response if generation is still pending —
     *  poll `getEvent` to await population. */
    hangoutLink?: string;
    htmlLink?: string;
    id: string;
    start?: CalendarEventDateTime;
    status?: string;
    summary?: string;
    /** Present only on events from the aggregate endpoint. Callers must
     *  forward it when editing or deleting — without it the request
     *  goes to the default account and either 404s or, worse, hits a
     *  same-named event on the wrong calendar. */
    _source?: CalendarEventSource;
    /** The id of the SERIES MASTER when this event is one occurrence of
     *  a repeating series. Our list calls use `singleEvents=true`, so a
     *  repeating event always arrives expanded into instances and the
     *  master is never returned directly — editing or deleting the whole
     *  series has to target this id instead of `id`. */
    recurringEventId?: string;
    /** When this instance was originally scheduled, before any per-
     *  occurrence move. Present on instances only. */
    originalStartTime?: CalendarEventDateTime;
    /** RRULE lines. Present on the series MASTER only — an instance
     *  carries none, which is why showing the current rule while editing
     *  requires fetching the master. */
    recurrence?: string[];
}

interface ErrorResponse {
    detail?: string;
}

export type CalendarErrorKind =
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required"
    | "other";

// Connection-state discriminators a caller may want to branch on (vs.
// the catch-all "other"). Shared so each service function's return
// union and `errorReturn` allow-list stay in sync.
type CalendarConnectionError =
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required";

const surfaceError = (
    error: unknown,
    setErrorMessage?: (value: string) => void
): CalendarErrorKind => {
    const messages = getMessages().integrations.errors;
    if (axios.isAxiosError(error)) {
        const detail = (error.response?.data as ErrorResponse | undefined)?.detail;
        if (detail === "google_not_connected") {
            setErrorMessage?.(messages.googleNotConnected);
            return "google_not_connected";
        }
        if (detail === "calendar_scope_missing") {
            // Frontend can present a "Grant Calendar access" button
            // that re-runs the connect-intent OAuth flow.
            setErrorMessage?.(messages.calendarScopeMissing);
            return "calendar_scope_missing";
        }
        if (detail === "google_reauth_required") {
            // The Google account row still exists (and may still carry
            // the calendar scope), but its refresh token is revoked or
            // expired. Callers present a "Reconnect Google Calendar"
            // button that re-runs the connect-intent OAuth flow, which
            // mints a fresh refresh token.
            setErrorMessage?.(messages.calendarReconnectRequired);
            return "google_reauth_required";
        }
        setErrorMessage?.(detail || messages.calendarRequestFailed);
    } else {
        setErrorMessage?.(messages.calendarRequestFailed);
    }
    return "other";
};

// Helper: map the surfaced error kind to the right discriminator
// return shape used by all calendar service functions. Keeps each
// function's catch block tight.
const errorReturn = <T extends CalendarConnectionError>(
    error: unknown,
    setErrorMessage: ((value: string) => void) | undefined,
    discriminators: readonly T[]
): T | null => {
    const kind = surfaceError(error, setErrorMessage);
    return (discriminators as readonly CalendarErrorKind[]).includes(kind) ? (kind as T) : null;
};

/** Build the `account_id` / `calendar_id` query params shared by the
 *  single-source read/write endpoints. Undefined entries are dropped by
 *  axios, and omitting `account_id` makes the server fall back to the
 *  user's default account — the pre-multi-account behaviour. */
const sourceParams = (opts: { calendarId?: string; accountId?: string }) => ({
    ...(opts.calendarId ? { calendar_id: opts.calendarId } : {}),
    ...(opts.accountId ? { account_id: opts.accountId } : {}),
});

export const listCalendars = async (
    accessToken: string,
    setErrorMessage?: (value: string) => void
): Promise<
    | CalendarListResponse
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required"
    | null
> => {
    try {
        const api = authApi(accessToken);
        if (!api) return null;
        const res = await api.get<CalendarListResponse>("/calendar/list/");
        return res.data;
    } catch (error) {
        return errorReturn(error, setErrorMessage, [
            "google_not_connected",
            "calendar_scope_missing",
            "google_reauth_required",
        ] as const);
    }
};

/**
 * Fetch events from several (account, calendar) pairs at once.
 *
 * This is what lets the calendar modal overlay a work account, a
 * personal account, and any calendars teammates have shared — all in
 * one grid. Two things the per-source `listEvents` can't do:
 *
 *   1. **Partial success.** Each source fails independently into
 *      `failed_sources`. A revoked refresh token on the personal
 *      account must never blank out the work calendar.
 *   2. **One round trip**, so the client isn't reconciling N loading
 *      states per repaint.
 *
 * An empty `sources` array short-circuits without a request — the user
 * has deselected everything, and asking the server for nothing would
 * return their default calendar, silently overriding that choice.
 */
export const listEventsAggregate = async (
    accessToken: string,
    opts: { from?: string; to?: string; sources: CalendarSource[]; maxResults?: number },
    setErrorMessage?: (value: string) => void
): Promise<
    | AggregateEventsResponse
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required"
    | null
> => {
    if (opts.sources.length === 0) return { items: [], failed_sources: [] };
    try {
        const api = authApi(accessToken);
        if (!api) return null;
        const res = await api.get<AggregateEventsResponse>("/calendar/events/aggregate/", {
            params: {
                from: opts.from,
                to: opts.to,
                sources: opts.sources.map((s) => sourceKey(s.accountId, s.calendarId)).join(","),
                max_results: opts.maxResults,
            },
        });
        return {
            items: res.data.items ?? [],
            failed_sources: res.data.failed_sources ?? [],
        };
    } catch (error) {
        return errorReturn(error, setErrorMessage, [
            "google_not_connected",
            "calendar_scope_missing",
            "google_reauth_required",
        ] as const);
    }
};

export const getEvent = async (
    accessToken: string,
    eventId: string,
    opts: { calendarId?: string; accountId?: string } = {},
    setErrorMessage?: (value: string) => void
): Promise<
    | CalendarEvent
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required"
    | "event_deleted_upstream"
    | null
> => {
    try {
        const api = authApi(accessToken);
        if (!api) return null;
        const res = await api.get<CalendarEvent>(`/calendar/events/${eventId}/`, {
            params: sourceParams(opts),
        });
        return res.data;
    } catch (error) {
        // Inspect the response before delegating: a 404 with
        // `event_deleted_upstream` is a domain signal (the linked event
        // was removed on Google), not a network failure. We surface it
        // as a distinct return so callers can offer "Unlink" instead of
        // a generic retry.
        if (axios.isAxiosError(error)) {
            const detail = (error.response?.data as ErrorResponse | undefined)?.detail;
            if (detail === "event_deleted_upstream") return "event_deleted_upstream";
        }
        return errorReturn(error, setErrorMessage, [
            "google_not_connected",
            "calendar_scope_missing",
            "google_reauth_required",
        ] as const);
    }
};

export const listEvents = async (
    accessToken: string,
    opts: { from?: string; to?: string; calendarId?: string; accountId?: string },
    setErrorMessage?: (value: string) => void
): Promise<
    | { items: CalendarEvent[] }
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required"
    | null
> => {
    try {
        const api = authApi(accessToken);
        if (!api) return null;
        const res = await api.get<{ items: CalendarEvent[] }>("/calendar/events/", {
            params: {
                from: opts.from,
                to: opts.to,
                ...sourceParams(opts),
            },
        });
        return res.data;
    } catch (error) {
        return errorReturn(error, setErrorMessage, [
            "google_not_connected",
            "calendar_scope_missing",
            "google_reauth_required",
        ] as const);
    }
};

export const createEvent = async (
    accessToken: string,
    body: {
        /** When true, attach a Google Meet `createRequest`. The
         *  response's `hangoutLink` may be absent if generation is
         *  pending — callers can poll `getEvent` to await it. */
        add_meet?: boolean;
        /** Other people to put on the event. Google still pushes
         *  the event to each attendee's calendar; the backend sets
         *  `sendUpdates=none` so no email invites go out. */
        attendees?: Array<{ email: string; displayName?: string }>;
        /** Which connected Google account owns the target calendar.
         *  Omit to use the user's default account. */
        account_id?: string;
        calendar_id?: string;
        description?: string;
        /** RRULE lines making this a repeating series. Omit (or send an
         *  empty array) for a one-off event. */
        recurrence?: string[];
        end: CalendarEventDateTime;
        start: CalendarEventDateTime;
        summary: string;
    },
    setErrorMessage?: (value: string) => void
): Promise<
    | CalendarEvent
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required"
    | null
> => {
    try {
        const api = authApi(accessToken);
        if (!api) return null;
        const res = await api.post<CalendarEvent>("/calendar/events/", body);
        return res.data;
    } catch (error) {
        return errorReturn(error, setErrorMessage, [
            "google_not_connected",
            "calendar_scope_missing",
            "google_reauth_required",
        ] as const);
    }
};

/**
 * PATCH an event.
 *
 * When `eventId` is a SERIES MASTER (i.e. you're editing "all events"),
 * do NOT pass `start`/`end`. The modal holds the start/end of the
 * INSTANCE the user opened, and sending those to the master moves the
 * whole series onto that occurrence's date — open the third standup,
 * fix a typo, choose "all events", and the series jumps two weeks
 * forward.
 */
export const updateEvent = async (
    accessToken: string,
    eventId: string,
    body: Partial<{
        /** Explicitly toggling: true adds a Meet (or keeps one),
         *  false removes any existing Meet. Omit entirely to leave
         *  the event's Meet state untouched. */
        add_meet: boolean;
        /** Sends the full attendee list — Google overwrites any
         *  existing list when this is provided. Omit to leave
         *  attendees untouched. The backend forwards
         *  `sendUpdates=none` when present to suppress email
         *  invites (chat-driven workflows do their own notify). */
        attendees: Array<{ email: string; displayName?: string }>;
        account_id: string;
        calendar_id: string;
        description: string;
        /** Only meaningful against the series MASTER. OMIT when editing a
         *  single occurrence — an empty array would end the repetition.
         *  See `updateEvent`'s note on start/end for the companion trap. */
        recurrence: string[];
        end: CalendarEventDateTime;
        start: CalendarEventDateTime;
        summary: string;
    }>,
    setErrorMessage?: (value: string) => void
): Promise<
    | CalendarEvent
    | "google_not_connected"
    | "calendar_scope_missing"
    | "google_reauth_required"
    | null
> => {
    try {
        const api = authApi(accessToken);
        if (!api) return null;
        const res = await api.patch<CalendarEvent>(`/calendar/events/${eventId}/`, body);
        return res.data;
    } catch (error) {
        return errorReturn(error, setErrorMessage, [
            "google_not_connected",
            "calendar_scope_missing",
            "google_reauth_required",
        ] as const);
    }
};

export const deleteEvent = async (
    accessToken: string,
    eventId: string,
    opts: { calendarId?: string; accountId?: string } = {},
    setErrorMessage?: (value: string) => void
): Promise<boolean> => {
    try {
        const api = authApi(accessToken);
        if (!api) return false;
        await api.delete(`/calendar/events/${eventId}/`, {
            params: sourceParams(opts),
        });
        return true;
    } catch (error) {
        surfaceError(error, setErrorMessage);
        return false;
    }
};
